import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Badge, Button, Card, Col, Container, Row, Spinner, Table } from 'react-bootstrap';
import toast from 'react-hot-toast';
import ContextHelper from '../ContextHooks/ContextHelper';
import { BASE_URL } from '../utils/apiBase';
import DatePicker from './date/DatePicker';
import CommonTable from './table/CommonTable';

const getDisplayValue = (value) => {
  if (value == null || value === '') {
    return '-';
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }

  if (Array.isArray(value)) {
    const normalizedValue = value.map((item) => getDisplayValue(item)).filter(Boolean).join(', ');
    return normalizedValue || '-';
  }

  if (typeof value === 'object') {
    if (typeof value.Alphabetic === 'string' && value.Alphabetic.trim()) {
      return value.Alphabetic;
    }

    const normalizedValue = Object.values(value)
      .map((item) => getDisplayValue(item))
      .filter(Boolean)
      .join(', ');

    return normalizedValue || '-';
  }

  return String(value);
};

const getStatusBadge = (jobStatus) => {
  if (!jobStatus) {
    return <Badge bg="secondary">Not sent</Badge>;
  }

  const state = String(jobStatus.State || jobStatus.state || '').toLowerCase();

  if (state.includes('success')) {
    return <Badge bg="success">Success</Badge>;
  }

  if (state.includes('fail') || state.includes('error')) {
    return <Badge bg="danger">Failed</Badge>;
  }

  if (state.includes('running') || state.includes('pending')) {
    return <Badge bg="warning" text="dark">{jobStatus.State || jobStatus.state}</Badge>;
  }

  if (jobStatus.jobId) {
    return <Badge bg="info">Submitted</Badge>;
  }

  return <Badge bg="secondary">Unknown</Badge>;
};

function StudyTransfer() {
  const { currentUser } = ContextHelper();
  const [studies, setStudies] = useState([]);
  const [isLoadingStudies, setIsLoadingStudies] = useState(true);
  const [isLoadingModalities, setIsLoadingModalities] = useState(false);
  const [modalities, setModalities] = useState([]);
  const [jobStatuses, setJobStatuses] = useState({});
  const [modalityStatuses, setModalityStatuses] = useState({});
  const [activeStudyId, setActiveStudyId] = useState('');
  const [activeJobId, setActiveJobId] = useState('');
  const [activeModality, setActiveModality] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [, setIsDataFetched] = useState(false);

  const fetchStudies = useCallback(async () => {
    try {
      setIsLoadingStudies(true);
      const today = new Date();
      const startDateTime = startDate ? new Date(startDate) : new Date(today);
      const endDateTime = endDate ? new Date(endDate) : new Date();
      startDateTime.setHours(0, 0, 0, 0);
      endDateTime.setHours(23, 59, 59, 999);

      const response = await axios.get(
        `${BASE_URL}apiv2/data?startdate=${startDateTime.toISOString()}&enddate=${endDateTime.toISOString()}`,
        {
          headers: {
            Authorization: `Bearer ${currentUser?.token}`,
            type: `${currentUser?.type || ''}`,
          },
        }
      );

      const formattedStudies = (response.data || []).map((item) => ({
        id: item.id,
        patientName: getDisplayValue(item.name),
        location: getDisplayValue(item.location),
        study: getDisplayValue(item.study),
        modality: getDisplayValue(item.modality),
        studyDate: getDisplayValue(item.StudyDate),
        orthancStudyId: item.newDrRemark || item.drRemark || '',
        studyInstanceUid: item.newStudyInstanceUID && item.newStudyInstanceUID !== 'NA'
          ? item.newStudyInstanceUID
          : item.studyUUID || '',
      }));

      setStudies(formattedStudies);
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || 'Failed to fetch studies');
      setStudies([]);
    } finally {
      setIsLoadingStudies(false);
    }
  }, [currentUser?.token, currentUser?.type, endDate, startDate]);

  const fetchModalities = useCallback(async () => {
    try {
      setIsLoadingModalities(true);
      const response = await axios.get(`${BASE_URL}modalities`, {
        headers: {
          Authorization: `Bearer ${currentUser?.token}`,
          type: `${currentUser?.type || ''}`,
        },
      });

      setModalities(response.data?.modalities || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || 'Failed to fetch modalities');
      setModalities([]);
    } finally {
      setIsLoadingModalities(false);
    }
  }, [currentUser?.token, currentUser?.type]);

  useEffect(() => {
    if (currentUser?.token) {
      fetchStudies();
      fetchModalities();
    }
  }, [currentUser?.token, fetchModalities, fetchStudies]);

  const handleSendStudy = async (study) => {
    if (!study?.orthancStudyId) {
      toast.error('Study ID is missing for this row');
      return;
    }

    try {
      setActiveStudyId(study.orthancStudyId);
      const response = await axios.post(
        `${BASE_URL}send-study`,
        { studyId: study.orthancStudyId },
        {
          headers: {
            Authorization: `Bearer ${currentUser?.token}`,
            type: `${currentUser?.type || ''}`,
          },
        }
      );

      const nextStatus = {
        jobId: response.data?.jobId,
        path: response.data?.path,
        state: 'Submitted',
      };

      setJobStatuses((prev) => ({
        ...prev,
        [study.orthancStudyId]: nextStatus,
      }));

      toast.success('Study sent successfully');
    } catch (error) {
      toast.error(error?.response?.data?.error || error.message || 'Failed to send study');
    } finally {
      setActiveStudyId('');
    }
  };

  const handleCheckJobStatus = async (study) => {
    const jobId = jobStatuses?.[study.orthancStudyId]?.jobId;

    if (!jobId) {
      toast.error('No job ID available for this study');
      return;
    }

    try {
      setActiveJobId(jobId);
      const response = await axios.get(`${BASE_URL}send-study-status/${jobId}`, {
        headers: {
          Authorization: `Bearer ${currentUser?.token}`,
          type: `${currentUser?.type || ''}`,
        },
      });

      setJobStatuses((prev) => ({
        ...prev,
        [study.orthancStudyId]: {
          ...prev[study.orthancStudyId],
          ...response.data,
        },
      }));
    } catch (error) {
      toast.error(error?.response?.data?.error || error.message || 'Failed to fetch job status');
    } finally {
      setActiveJobId('');
    }
  };

  const handleCheckModalityStatus = async (modalityName) => {
    try {
      setActiveModality(modalityName);
      const response = await axios.get(`${BASE_URL}modalities/${encodeURIComponent(modalityName)}/status`, {
        headers: {
          Authorization: `Bearer ${currentUser?.token}`,
          type: `${currentUser?.type || ''}`,
        },
      });

      setModalityStatuses((prev) => ({
        ...prev,
        [modalityName]: response.data,
      }));
    } catch (error) {
      toast.error(error?.response?.data?.error || error.message || 'Failed to fetch modality status');
    } finally {
      setActiveModality('');
    }
  };

  const studyColumns = [
    { accessor: 'patientName', label: 'Patient' },
    { accessor: 'study', label: 'Study' },
    { accessor: 'location', label: 'Location' },
    { accessor: 'modality', label: 'Modality' },
    {
      accessor: 'studyInstanceUid',
      label: 'Study UID',
      render: (value) => <div className="text-break">{value || '-'}</div>,
    },
    {
      accessor: 'status',
      label: 'Status',
      sorting: false,
      render: (_, row) => {
        const currentJobStatus = jobStatuses[row.orthancStudyId];

        return (
          <div className="d-flex flex-column gap-1">
            {getStatusBadge(currentJobStatus)}
            {currentJobStatus?.jobId && <small className="text-muted">Job: {currentJobStatus.jobId}</small>}
            {currentJobStatus?.Progress !== undefined && (
              <small className="text-muted">Progress: {currentJobStatus.Progress}%</small>
            )}
          </div>
        );
      },
    },
    {
      accessor: 'actions',
      label: 'Actions',
      sorting: false,
      render: (_, row) => {
        const currentJobStatus = jobStatuses[row.orthancStudyId];

        return (
          <div className="d-flex flex-wrap gap-2">
            <Button
              size="sm"
              className="base-color-bg border-0"
              onClick={() => handleSendStudy(row)}
              disabled={activeStudyId === row.orthancStudyId || !row.orthancStudyId}
            >
              {activeStudyId === row.orthancStudyId ? <Spinner size="sm" /> : 'Send Study'}
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => handleCheckJobStatus(row)}
              disabled={!currentJobStatus?.jobId || activeJobId === currentJobStatus?.jobId}
            >
              {activeJobId === currentJobStatus?.jobId ? <Spinner size="sm" /> : 'Check Job'}
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <section className="py-4">
      <Container fluid>
        <Row className="g-4">
          <Col xl={8}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
                  <div>
                    <h4 className="mb-1 fs-20">Send Studies</h4>
                    <p className="text-muted mb-0 fs-14">Send studies to the configured destination and track background jobs.</p>
                  </div>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <DatePicker
                      setStartDate={setStartDate}
                      setEndDate={setEndDate}
                      setIsDataFetched={setIsDataFetched}
                    />
                    <Button className="base-color-bg border-0" onClick={fetchStudies} disabled={isLoadingStudies}>
                      {isLoadingStudies ? <Spinner size="sm" /> : 'Refresh Studies'}
                    </Button>
                  </div>
                </div>

                <CommonTable data={studies} columns={studyColumns} loading={isLoadingStudies} />
              </Card.Body>
            </Card>
          </Col>

          <Col xl={4}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
                  <div>
                    <h4 className="mb-1 fs-20">Modalities</h4>
                    <p className="text-muted mb-0 fs-14">Inspect configured destinations and run connection checks.</p>
                  </div>
                  <Button variant="outline-dark" onClick={fetchModalities} disabled={isLoadingModalities}>
                    {isLoadingModalities ? <Spinner size="sm" /> : 'Refresh'}
                  </Button>
                </div>

                <div className="table-responsive">
                  <Table hover bordered className="align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Modality</th>
                        <th>Connection</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoadingModalities ? (
                        <tr>
                          <td colSpan={3} className="text-center py-4">
                            <Spinner />
                          </td>
                        </tr>
                      ) : modalities.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="text-center py-4">No modalities found</td>
                        </tr>
                      ) : (
                        modalities.map((modalityName) => {
                          const modalityStatus = modalityStatuses[modalityName];

                          return (
                            <tr key={modalityName}>
                              <td>{modalityName}</td>
                              <td>
                                {modalityStatus ? (
                                  modalityStatus.connected ? (
                                    <Badge bg="success">Connected</Badge>
                                  ) : (
                                    <Badge bg="danger">Disconnected</Badge>
                                  )
                                ) : (
                                  <Badge bg="secondary">Unchecked</Badge>
                                )}
                              </td>
                              <td>
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={() => handleCheckModalityStatus(modalityName)}
                                  disabled={activeModality === modalityName}
                                >
                                  {activeModality === modalityName ? <Spinner size="sm" /> : 'Check'}
                                </Button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>

                <div className="mt-3 d-flex flex-column gap-2">
                  {Object.entries(modalityStatuses).map(([modalityName, modalityStatus]) => (
                    <Card key={modalityName} className="bg-light border-0">
                      <Card.Body className="py-2 px-3">
                        <div className="fw-semibold mb-1">{modalityName}</div>
                        <div className="small text-muted">AET: {modalityStatus?.configuration?.AET || '-'}</div>
                        <div className="small text-muted">Host: {modalityStatus?.configuration?.Host || '-'}</div>
                        <div className="small text-muted">Port: {modalityStatus?.configuration?.Port || '-'}</div>
                        {!modalityStatus?.connected && modalityStatus?.error && (
                          <div className="small text-danger mt-1 text-break">{JSON.stringify(modalityStatus.error)}</div>
                        )}
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </section>
  );
}

export default StudyTransfer;